// Klub panel — Klub főoldal (Figma OP_01): napi mutatók, mai foglalások, táblák állapota, gyors műveletek.
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Icon } from '../icons';
import { fmtInt } from '../data';
import { Badge, Button, Card, Empty, PageHeader, SectionTitle, Spinner, StatCard } from '../ui';
import { bookingPrice, bookingTitle, openWindow, useBookings, useBoards, useClub, usePeople } from './clubData';
import { BookingDrawer, hhmm, KIND_LABEL, NewBookingModal, statusOf } from './clubUi';

const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

export default function ClubHome() {
  const { club, clubs } = useClub();
  const { user } = useAuth();
  const nav = useNavigate();
  const boards = useBoards(club?.id);
  const bookings = useBookings(club?.id, 3, 3);
  const [openId, setOpenId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const who = usePeople((bookings ?? []).map((b) => b.createdBy));

  const now = new Date();
  const yesterday = new Date(now.getTime() - 864e5);

  const m = useMemo(() => {
    if (!boards || !bookings) return null;
    const live = bookings.filter((b) => b.state !== 'cancelled');
    const today = live.filter((b) => sameDay(b.start, now));
    const yest = live.filter((b) => sameDay(b.start, yesterday));
    const busyNow = new Set(live.filter((b) => b.start <= now && now < b.end).map((b) => b.board));
    const free = boards.filter((b) => b.state === 'free' && !busyNow.has(b.order)).length;
    const rev = today.reduce((s, b) => s + bookingPrice(b, boards), 0);
    const revY = yest.reduce((s, b) => s + bookingPrice(b, boards), 0);
    const w = club ? openWindow(club.hours, now) : null;
    const openMin = w ? (w.to.getTime() - w.from.getTime()) / 60000 : 0;
    const used = today.reduce((s, b) => s + b.durationMin, 0);
    const util = openMin && boards.length ? Math.min(100, Math.round((used / (openMin * boards.length)) * 100)) : 0;
    return { today, yestCount: yest.length, busyNow, free, rev, revY, util, w };
  }, [boards, bookings, club]); // eslint-disable-line react-hooks/exhaustive-deps

  if (clubs && clubs.length === 0) {
    return (
      <div>
        <PageHeader title="Klub panel" />
        <Card><Empty>Ehhez a fiókhoz még nem tartozik klub. A klubot a Dartiqon mobilappban lehet regisztrálni.</Empty></Card>
      </div>
    );
  }
  if (!club || !m || !boards) return <Spinner />;

  const first = user?.displayName || club.name;
  const todaysList = [...m.today, ...bookings!.filter((b) => b.state === 'cancelled' && sameDay(b.start, now))]
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  const tile = (order: number) => {
    const bd = boards.find((x) => x.order === order)!;
    const st = bd.state === 'maintenance' ? { t: 'Karbantartás', c: '#d9a514' }
      : bd.state === 'closed' ? { t: 'Lezárva', c: '#8a8f99' }
        : m.busyNow.has(order) ? { t: 'Foglalt', c: '#00AFF2' } : { t: 'Szabad', c: '#2bc48a' };
    return (
      <div key={bd.id} className="h-[58px] rounded-xl flex flex-col items-center justify-center border"
        style={{ background: `${st.c}14`, borderColor: `${st.c}55` }}>
        <span className="text-[16px] font-bold text-white leading-5">{order}.</span>
        <span className="text-[10px]" style={{ color: st.c }}>{st.t}</span>
      </div>
    );
  };

  const delta = m.today.length - m.yestCount;
  const openB = bookings?.find((b) => b.id === openId) ?? null;
  return (
    <div>
      <PageHeader
        title={`Üdvözöljük, ${first}!`}
        sub={`Ön üzemeltetői hozzáféréssel rendelkezik · ${club.name}`}
        right={<Button icon="CalendarBlank" onClick={() => nav('/club/boards?tab=calendar')}>Foglalási naptár</Button>}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard icon="CalendarBlank" tone="cyan" label="Mai foglalások" value={fmtInt(m.today.length)}
          delta={delta ? { text: `${Math.abs(delta)} a tegnapihoz képest`, up: delta > 0 } : null} />
        <StatCard icon="SquaresFour" tone="green" label="Szabad táblák most" value={`${m.free} / ${boards.length}`} valueColor="#2bc48a" />
        <StatCard icon="Coins" tone="gold" label="Mai bevétel" value={`${fmtInt(m.rev)} Ft`} valueColor="#d9a514"
          delta={m.revY ? { text: `${Math.abs(Math.round(((m.rev - m.revY) / m.revY) * 100))}%`, up: m.rev >= m.revY } : null} />
        <Card className="!p-[21px] min-h-[122px]">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-[10px] bg-[#a855f7]/[0.14] text-[#a855f7]"><Icon name="TrendUp" size={20} /></span>
            <span className="text-[13px] font-medium text-[#B8B8C3]">Kihasználtság</span>
          </div>
          <div className="mt-2.5 text-[28px] leading-[34px] font-bold text-[#a855f7]">{m.util}%</div>
          <div className="mt-2 text-[12px] text-[#8a8f99]">{m.w ? `Ma ${hhmm(m.w.from)}–${hhmm(m.w.to)}` : 'Ma zárva'}</div>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-4 mt-6 items-start">
        <Card className="!p-5">
          <SectionTitle right={
            <button type="button" onClick={() => nav('/club/boards?tab=calendar')} className="inline-flex items-center gap-1 text-[13px] font-medium text-[#00AFF2] hover:underline">
              Naptár megnyitása <Icon name="ArrowRight" size={14} />
            </button>
          }>Mai foglalások</SectionTitle>
          {todaysList.length === 0 && <Empty>Mára még nincs foglalás.</Empty>}
          {todaysList.map((b, i, arr) => {
            const st = statusOf(b);
            const n = b.guestName ? 1 : Math.max(1, b.participantIds.length);
            return (
              <button key={b.id} type="button" onClick={() => setOpenId(b.id)}
                className={`w-full flex items-center gap-3 py-3 text-left hover:bg-white/[0.02] ${i < arr.length - 1 ? 'border-b border-[#31383D]' : ''}`}>
                <span className="w-[86px] shrink-0 rounded-lg bg-[#1d232a] py-1.5 text-center">
                  <span className="block text-[13px] font-semibold text-white">{hhmm(b.start)}–{hhmm(b.end)}</span>
                  <span className="block text-[11px] text-[#8a8f99]">{b.board}. tábla</span>
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] font-semibold text-white truncate">{bookingTitle(b, who)} · {n} fő</span>
                  <span className="block text-[12px] text-[#8a8f99]">{KIND_LABEL[b.kind]}</span>
                </span>
                <Badge tone={st.tone}>{st.t}</Badge>
                <Icon name="CaretRight" size={16} className="text-[#8a8f99]" />
              </button>
            );
          })}
        </Card>

        <Card className="!p-5">
          <SectionTitle>Táblák állapota</SectionTitle>
          {boards.length === 0 ? <Empty>Még nincs tábla.</Empty> : (
            <div className="grid grid-cols-4 gap-2">{boards.map((b) => tile(b.order))}</div>
          )}
          <div className="mt-5 pt-4 border-t border-[#31383D]">
            <h3 className="text-[13px] font-extrabold italic uppercase text-white mb-3">Gyors műveletek</h3>
            {[
              { icon: 'Plus', t: 'Új foglalás rögzítése', on: () => setAdding(true) },
              { icon: 'Wrench', t: 'Tábla karbantartásba', on: () => nav('/club/boards') },
              { icon: 'PencilSimpleLine', t: 'Esemény létrehozása', on: () => nav('/club/customize?tab=events&new=1') },
            ].map((a) => (
              <button key={a.t} type="button" onClick={a.on}
                className="w-full flex items-center gap-3 h-[44px] px-3 mb-2 rounded-xl bg-[#1d232a] text-left text-[13px] font-medium text-white hover:bg-[#232a32]">
                <Icon name={a.icon} size={16} className="text-[#00AFF2]" />
                <span className="flex-1">{a.t}</span>
                <Icon name="CaretRight" size={14} className="text-[#8a8f99]" />
              </button>
            ))}
          </div>
        </Card>
      </div>

      {openB && <BookingDrawer b={openB} boards={boards} club={club} onClose={() => setOpenId(null)} />}
      {adding && <NewBookingModal club={club} boards={boards} bookings={bookings ?? []} onClose={() => setAdding(false)} />}
    </div>
  );
}
